// src/components/fraudWarnings/FraudWarningsPanel.js - Updated with Email Header Styling
import React, { useState, useEffect, useCallback } from "react";
import {
  Paper,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Button,
  IconButton,
  Collapse,
  Grid,
  Tooltip,
  FormControl,
  Select,
  Menu,
  MenuItem,
  InputLabel,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Badge,
  TablePagination,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  AlertTitle,
  LinearProgress,
} from "@mui/material";

import RefreshIcon from "@mui/icons-material/Refresh";
import WarningIcon from "@mui/icons-material/Warning";
import InfoIcon from "@mui/icons-material/Info";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import BlockIcon from "@mui/icons-material/Block";
import LockIcon from "@mui/icons-material/Lock";
import DeleteIcon from "@mui/icons-material/Delete";
import NotificationAddIcon from "@mui/icons-material/NotificationAdd";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import CommentIcon from "@mui/icons-material/Comment";
import HistoryIcon from "@mui/icons-material/History";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import { useEnvironment } from "../../context/EnvironmentContext";
import apiClient from "../../services/EnhancedApiClient";
import {
  Download as DownloadIcon,
  FileDownload as FileDownloadIcon,
  TableChart as TableChartIcon,
} from "@mui/icons-material";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CancelIcon from '@mui/icons-material/Cancel';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import GroupSharp from '@mui/icons-material/GroupSharp';

// All the existing utility functions and components would stay the same...
const calculateRiskLevel = (differences) => {
  if (!differences || typeof differences !== "object") {
    return "low";
  }

  let riskScore = 0;

  if (differences.state?.isDifferent) riskScore += 5;
  if (differences.name?.isDifferent) riskScore += 4;
  if (differences.email?.isDifferent) riskScore += 3;
  if (differences.phone?.isDifferent) riskScore += 3;
  if (differences.address?.isDifferent) riskScore += 2;
  if (differences.city?.isDifferent) riskScore += 2;
  if (differences.zip?.isDifferent) riskScore += 1;

  if (riskScore >= 8) return "high";
  if (riskScore >= 5) return "medium";
  return "low";
};

const safeToLowerCase = (value) => {
  if (value === null || value === undefined) {
    return "";
  }
  if (typeof value === "string") {
    return value.toLowerCase();
  }
  return String(value).toLowerCase();
};

const formatWarningType = (type, warningType) => {
  if (warningType) return warningType;
  if (!type) return "Unknown";

  switch (safeToLowerCase(type)) {
    case "address":
    case "address_mismatch":
      return "Address Mismatch";
    case "name":
      return "Name Mismatch";
    case "location":
      return "Suspicious Location";
    case "database_match":
      return "Database Match";
    case "identity_plausibility":
      return "Identity Plausibility";
    default:
      return type.charAt(0).toUpperCase() + type.slice(1);
  }
};

const formatPlausibilityReason = (reason) => {
  if (!reason) return null;
  const reasonMap = {
    'no_name_relationship_found': 'No name relationship found',
    'weak_match': 'Weak match between name and email',
    'partial_match': 'Partial match between name and email',
  };
  return reasonMap[reason] || reason.replace(/_/g, ' ');
};

const formatTimestamp = (timestamp) => {
  const date = new Date(timestamp);

  return date.toLocaleString("en-US", {
    year: "numeric",
    month: "numeric",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
    timeZone: "America/New_York",
  });
};

// Comparison component for expanded view
const ComparisonDetails = ({ billingInfo, shippingInfo }) => {
  const safeBilling = billingInfo || {
    first_name: "N/A",
    last_name: "N/A",
    email: "N/A",
    phone: "N/A",
    address1: "N/A",
    city: "N/A",
    state: "N/A",
    zip: "N/A",
  };

  const safeShipping = shippingInfo || {
    first_name: "N/A",
    last_name: "N/A",
    email: "N/A",
    phone: "N/A",
    address1: "N/A",
    city: "N/A",
    state: "N/A",
    zip: "N/A",
  };

  const differences = {
    name: {
      billing: `${safeBilling.first_name || "N/A"} ${safeBilling.last_name || "N/A"
        }`,
      shipping: `${safeShipping.first_name || "N/A"} ${safeShipping.last_name || "N/A"
        }`,
      isDifferent:
        (safeBilling.first_name || "") !== (safeShipping.first_name || "") ||
        (safeBilling.last_name || "") !== (safeShipping.last_name || ""),
    },
    email: {
      billing: safeBilling.email || "N/A",
      shipping: safeShipping.email || "N/A",
      isDifferent: (safeBilling.email || "") !== (safeShipping.email || ""),
    },
    phone: {
      billing: safeBilling.phone || "N/A",
      shipping: safeShipping.phone || "N/A",
      isDifferent: (safeBilling.phone || "") !== (safeShipping.phone || ""),
    },
    address: {
      billing: safeBilling.address1 || "N/A",
      shipping: safeShipping.address1 || "N/A",
      isDifferent:
        (safeBilling.address1 || "") !== (safeShipping.address1 || ""),
    },
    city: {
      billing: safeBilling.city || "N/A",
      shipping: safeShipping.city || "N/A",
      isDifferent: (safeBilling.city || "") !== (safeShipping.city || ""),
    },
    state: {
      billing: safeBilling.state || "N/A",
      shipping: safeShipping.state || "N/A",
      isDifferent: (safeBilling.state || "") !== (safeShipping.state || ""),
    },
    zip: {
      billing: safeBilling.zip || "N/A",
      shipping: safeShipping.zip || "N/A",
      isDifferent: (safeBilling.zip || "") !== (safeShipping.zip || ""),
    },
  };

  const riskLevel = calculateRiskLevel(differences);

  if (!billingInfo && !shippingInfo) {
    return (
      <Box sx={{ margin: 2, textAlign: "center" }}>
        <Typography variant="body2" color="text.secondary">
          No address information available for comparison
        </Typography>
      </Box>
    );
  }

  if (!billingInfo || !shippingInfo) {
    return (
      <Box sx={{ margin: 2 }}>
        <Alert severity="warning" sx={{ mb: 2 }}>
          {!billingInfo
            ? "Billing information is missing"
            : "Shipping information is missing"}
        </Alert>
        <Typography variant="body2" color="text.secondary">
          Address comparison requires both billing and shipping information
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ margin: 2 }}>
      <Grid container spacing={2} mb={2}>
        <Grid item xs={12}>
          <Chip
            label={`Risk Level: ${riskLevel.toUpperCase()}`}
            color={
              riskLevel === "high"
                ? "error"
                : riskLevel === "medium"
                  ? "warning"
                  : "info"
            }
            icon={<WarningIcon />}
            sx={{ mr: 1 }}
          />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Risk assessment based on type and number of differences found
          </Typography>
        </Grid>
      </Grid>

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ backgroundColor: "#f3f4f6", fontWeight: 600 }}>
                Field
              </TableCell>
              <TableCell sx={{ backgroundColor: "#f3f4f6", fontWeight: 600 }}>
                Billing Address
              </TableCell>
              <TableCell sx={{ backgroundColor: "#f3f4f6", fontWeight: 600 }}>
                Shipping Address
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {Object.entries(differences).map(([field, data]) => (
              <TableRow
                key={field}
                sx={{
                  // Use the exact same colors as the email template
                  backgroundColor: data.isDifferent ? "#fecaca" : "#dcfce7",
                }}
              >
                <TableCell sx={{ fontWeight: "bold" }}>
                  {field.charAt(0).toUpperCase() + field.slice(1)}
                </TableCell>
                <TableCell>{data.billing}</TableCell>
                <TableCell>{data.shipping}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

// Database Match Details component for expanded view
const DatabaseMatchDetails = ({ warning }) => {
  const [expandedRecord, setExpandedRecord] = React.useState(null);

  const handleRecordToggle = (index) => {
    setExpandedRecord(expandedRecord === index ? null : index);
  };

  return (
    <Box sx={{ margin: 2 }}>
      {/* Risk Summary */}
      <Grid container spacing={2} mb={2}>
        <Grid item xs={12}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Chip
              label={`Match(${warning.matchCount || 0})`}
              color="error"
              sx={{ fontWeight: 'bold', fontSize: '1rem' }}
            />
            <Chip
              label={warning.riskLevel?.toUpperCase() || 'UNKNOWN'}
              color="error"
              variant="outlined"
            />
          </Box>
          <Typography variant="body2">
            Customer data matched <strong>{warning.matchCount || 0} fields</strong> across{' '}
            <strong>{warning.matchedRecordCount || 0} confirmed fraud records</strong>.
          </Typography>
        </Grid>
      </Grid>

      <Divider sx={{ my: 2 }} />

      {/* Field Match Summary */}
      <Typography variant="h6" gutterBottom>
        Field Match Summary
      </Typography>
      <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: "#f9fafb" }}>
              <TableCell sx={{ fontWeight: 600 }}>Field</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Value</TableCell> 
              <TableCell align="right" sx={{ fontWeight: 600 }}>Match Count</TableCell>
            </TableRow>
          </TableHead>
<TableBody>
  {warning.fieldMatches && Object.entries(warning.fieldMatches)
    .filter(([field]) => !['city', 'state', 'zip'].includes(field))
    .map(([field, data]) => {
const fieldValue = data.value || 'N/A';

      return (
    <TableRow
      key={field}
      sx={{
        bgcolor: data.matched ? 'rgba(76, 175, 80, 0.1)' : 'transparent'
      }}
    >
                <TableCell sx={{ textTransform: 'capitalize' }}>{field}</TableCell>
                <TableCell>
                  {data.matched ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <CheckCircleIcon fontSize="small" color="success" />
                      <Typography variant="body2" color="success.main">Matched</Typography>
                    </Box>
                  ) : (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <CancelIcon fontSize="small" color="disabled" />
                      <Typography variant="body2" color="text.disabled">No Match</Typography>
                    </Box>
                  )}
                </TableCell>
                          <TableCell>  {/* ✅ ADD THIS CELL */}
            <Typography 
              variant="body2" 
              sx={{ 
                fontFamily: 'monospace',
                fontSize: '0.9em',
                color: data.matched ? 'success.dark' : 'text.secondary'
              }}
            >
              {fieldValue}
            </Typography>
          </TableCell>
                <TableCell align="right">
                  <strong>{data.count || 0}</strong>
                </TableCell>
        </TableRow>
      );
    })}
</TableBody>
        </Table>
      </TableContainer>

      <Divider sx={{ my: 2 }} />

      {/* Matched Fraud Records */}
      <Typography variant="h6" gutterBottom>
        Matched Fraud Records ({warning.matchedRecordCount || 0})
      </Typography>

      {warning.matchedRecords && warning.matchedRecords.length > 0 ? (
        warning.matchedRecords.map((record, index) => (
          <Accordion
            key={index}
            expanded={expandedRecord === index}
            onChange={() => handleRecordToggle(index)}
            sx={{ mb: 1 }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Chip
                  label={`${record.matchCount || 0} fields`}
                  size="small"
                  color="error"
                />
                <Typography variant="body2">
                  <strong>Order:</strong> {record.fraudOrderId || 'N/A'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {record.fraudDate || ''}
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">Name</Typography>
                  <Typography variant="body2">{record.fraudName || 'N/A'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">Billing Email</Typography>
                  <Typography variant="body2">{record.fraudBillingEmail || 'N/A'}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">Matched Fields</Typography>
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                    {record.matchedFields && record.matchedFields.map((field) => (
                      <Chip
                        key={field}
                        label={field}
                        size="small"
                        color="success"
                        variant="outlined"
                      />
                    ))}
                  </Box>
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>
        ))
      ) : (
        <Typography variant="body2" color="text.secondary">
          No matched records available
        </Typography>
      )}

      {/* Pattern Alert */}
      {warning.matchedRecordCount >= 3 && (
        <Alert severity="error" sx={{ mt: 2 }}>
          <AlertTitle>⚠️ Serial Fraudster Pattern Detected</AlertTitle>
          This customer matches <strong>{warning.matchedRecordCount} previous fraud attempts</strong>.
          Strong recommendation to <strong>BLOCK</strong> this order.
        </Alert>
      )}
    </Box>
  );
};

// Identity Plausibility Details component for expanded view
const IdentityPlausibilityDetails = ({ warning }) => {
  const emailGibberish = warning.emailGibberish || {};
  const nameEmailConsistency = warning.nameEmailConsistency || {};
  const nameGibberish = warning.nameGibberish || {};
  const combinedScore = warning.combinedScore ?? 0;
  const threshold = warning.threshold ?? 6;
  const scoreRatio = Math.min(combinedScore / 15, 1); // max possible ~15
  const overThreshold = combinedScore > threshold;

  const scoreColor = overThreshold
    ? combinedScore >= threshold + 4
      ? '#d32f2f'
      : '#ed6c02'
    : '#2e7d32';

  return (
    <Box sx={{ margin: 2 }}>
      {/* Header Section */}
      <Grid container spacing={1.5} mb={2}>
        <Grid item xs={12} sm={6}>
          <Typography variant="caption" color="text.secondary">Email Analyzed</Typography>
          <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
            {warning.email || warning.customerEmail || 'N/A'}
          </Typography>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Typography variant="caption" color="text.secondary">Customer Name</Typography>
          <Typography variant="body2">{warning.customerName || 'N/A'}</Typography>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Typography variant="caption" color="text.secondary">Order Number</Typography>
          <Typography variant="body2">{warning.orderNumber || 'N/A'}</Typography>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Typography variant="caption" color="text.secondary">Combined Score</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip
              label={`Score: ${combinedScore} / Threshold: ${threshold}`}
              sx={{
                fontWeight: 'bold',
                bgcolor: scoreColor,
                color: '#fff',
              }}
            />
          </Box>
        </Grid>
      </Grid>

      {/* Score Bar */}
      <Box sx={{ mb: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
          <Typography variant="caption" color="text.secondary">Risk Level</Typography>
          <Typography variant="caption" color="text.secondary">
            {combinedScore} / 15
          </Typography>
        </Box>
        <LinearProgress
          variant="determinate"
          value={scoreRatio * 100}
          sx={{
            height: 8,
            borderRadius: 4,
            bgcolor: '#e0e0e0',
            '& .MuiLinearProgress-bar': {
              borderRadius: 4,
              bgcolor: scoreColor,
            },
          }}
        />
        {/* Threshold marker */}
        <Box sx={{ position: 'relative', height: 12 }}>
          <Box
            sx={{
              position: 'absolute',
              left: `${(threshold / 15) * 100}%`,
              transform: 'translateX(-50%)',
              fontSize: '0.65rem',
              color: 'text.secondary',
            }}
          >
            ▲ threshold
          </Box>
        </Box>
      </Box>

      <Divider sx={{ my: 2 }} />

      {/* Signal Breakdown Table */}
      <Typography variant="h6" gutterBottom>Signal Breakdown</Typography>
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f9fafb' }}>
              <TableCell sx={{ fontWeight: 600 }}>Signal</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Score</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Details</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {/* Email Gibberish */}
            <TableRow sx={{ bgcolor: (emailGibberish.total || 0) >= 4 ? 'rgba(211,47,47,0.06)' : 'transparent' }}>
              <TableCell sx={{ fontWeight: 'bold' }}>Email Gibberish</TableCell>
              <TableCell>
                <Chip
                  label={`${emailGibberish.total ?? '—'} / 11`}
                  size="small"
                  color={(emailGibberish.total || 0) >= 4 ? 'error' : 'default'}
                  variant="outlined"
                />
              </TableCell>
              <TableCell>
                <Typography variant="body2" color="text.secondary">
                  Vowel ratio: {emailGibberish.vowelRatio ?? '—'}%,{' '}
                  Longest consonant run: {emailGibberish.longestConsonantRun ?? '—'},{' '}
                  Trailing digits: {emailGibberish.trailingDigits ?? '—'}
                </Typography>
              </TableCell>
            </TableRow>
            {/* Name/Email Consistency */}
            <TableRow sx={{ bgcolor: (nameEmailConsistency.score || 0) >= 2 ? 'rgba(211,47,47,0.06)' : 'transparent' }}>
              <TableCell sx={{ fontWeight: 'bold' }}>Name/Email Consistency</TableCell>
              <TableCell>
                <Chip
                  label={`${nameEmailConsistency.score ?? '—'} / 3`}
                  size="small"
                  color={(nameEmailConsistency.score || 0) >= 2 ? 'error' : 'default'}
                  variant="outlined"
                />
              </TableCell>
              <TableCell>
                <Typography variant="body2" color="text.secondary">
                  {formatPlausibilityReason(nameEmailConsistency.details?.reason) || 'No details available'}
                </Typography>
              </TableCell>
            </TableRow>
            {/* Name Gibberish */}
            <TableRow sx={{ bgcolor: (nameGibberish.rawTotal || 0) >= 2 ? 'rgba(211,47,47,0.06)' : 'transparent' }}>
              <TableCell sx={{ fontWeight: 'bold' }}>Name Gibberish</TableCell>
              <TableCell>
                <Chip
                  label={`${nameGibberish.rawTotal ?? '—'} / 4 (weighted: ${nameGibberish.weighted ?? '—'})`}
                  size="small"
                  color={(nameGibberish.rawTotal || 0) >= 2 ? 'error' : 'default'}
                  variant="outlined"
                />
              </TableCell>
              <TableCell>
                <Typography variant="body2" color="text.secondary">
                  Vowel ratio: {nameGibberish.vowelRatio ?? '—'}%,{' '}
                  Longest consonant run: {nameGibberish.longestConsonantRun ?? '—'}
                </Typography>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

// Notes History Component
const NotesHistory = ({ warning }) => {
  console.log("NotesHistory component received warning:", {
    warningId: warning.id || warning.orderNumber,
    hasStatusHistory: !!(
      warning.statusHistory && warning.statusHistory.length > 0
    ),
    statusHistoryLength: warning.statusHistory?.length || 0,
    hasReviewNotes: !!warning.reviewNotes,
    reviewNotes: warning.reviewNotes,
    statusHistory: warning.statusHistory,
  });

  const { statusHistory = [] } = warning;

  // Filter statusHistory to only show entries with notes
  const notesEntries = statusHistory.filter(
    (entry) =>
      entry.action === "add-notes" ||
      (entry.notes && entry.notes.trim().length > 0)
  );

  // Show component if we have any notes entries OR reviewNotes
  const hasAnyNotes =
    notesEntries.length > 0 ||
    (warning.reviewNotes && warning.reviewNotes.trim());

  if (!hasAnyNotes) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
        No review history available
      </Typography>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Box display="flex" alignItems="center" gap={1} mb={2}>
        <HistoryIcon color="action" />
        <Typography variant="subtitle2">Review History</Typography>
        <Chip
          label={`${notesEntries.length} notes`}
          size="small"
          color="info"
        />
      </Box>

      {/* Show reviewNotes if available */}
      {warning.reviewNotes && (
        <Box mb={2} p={2} bgcolor="#f5f5f5" borderRadius={1}>
          <Typography
            variant="caption"
            color="text.secondary"
            display="block"
            mb={1}
          >
            Cumulative Notes:
          </Typography>
          <Typography
            variant="body2"
            component="pre"
            sx={{ whiteSpace: "pre-wrap" }}
          >
            {warning.reviewNotes}
          </Typography>
        </Box>
      )}

      <List dense>
        {notesEntries.map((entry, index) => (
          <ListItem
            key={entry.noteId || entry.reviewId || index}
            sx={{ pl: 0 }}
          >
            <ListItemText
              primary={
                <Box display="flex" alignItems="center" gap={1}>
                  <Chip
                    label={entry.status || entry.action}
                    size="small"
                    color={
                      entry.status === "blocked"
                        ? "error"
                        : entry.status === "reviewed-ok"
                          ? "success"
                          : entry.action === "add-notes"
                            ? "info"
                            : "default"
                    }
                  />
                  <Typography variant="caption" color="text.secondary">
                    {formatTimestamp(entry.timestamp)} by {entry.user}
                  </Typography>
                </Box>
              }
              secondary={entry.notes || "No notes provided"}
            />
          </ListItem>
        ))}
      </List>
    </Box>
  );
};

const FraudWarningsPanel = () => {
  const [warnings, setWarnings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedRows, setExpandedRows] = useState({});
  const [lastUpdated, setLastUpdated] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [riskLevel, setRiskLevel] = useState("all");
  const [warningType, setWarningType] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all-except-reviewed");
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);
  const [newWarningsCount, setNewWarningsCount] = useState(0);
  const [reviewDialog, setReviewDialog] = useState({
    open: false,
    warning: null,
    notes: "",
  });
  const [clearWarningsDialog, setClearWarningsDialog] = useState(false);
  const [clearingWarnings, setClearingWarnings] = useState(false);
  const [exportMenuAnchor, setExportMenuAnchor] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const { environment } = useEnvironment();

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const fetchWarnings = useCallback(
    async (force = false, additionalOptions = {}) => {
      try {
        if (force) {
          setRefreshing(true);
        } else {
          setLoading(true);
        }

        const fetchOptions = {
          riskLevel: riskLevel !== "all" ? riskLevel : undefined,
          warningType: warningType !== "all" ? warningType : undefined,
          force,
          timestamp: additionalOptions.timestamp || Date.now(),
          ...additionalOptions,
        };

        if (additionalOptions.forceReload) {
          apiClient.clearCacheForEndpoint("/api/admin/fraud-warnings");
        }

        const response = await apiClient.fetchFraudWarnings(fetchOptions);

        const newCount =
          response.warnings?.filter((w) => w.status === "new").length || 0;
        setNewWarningsCount(newCount);

        if (notificationsEnabled && newCount > 0) {
          showNotification(`${newCount} new fraud warning(s) detected!`);
        }

        setWarnings(response.warnings || []);
        setLastUpdated(new Date());
        setError(null);
      } catch (err) {
        console.error("Error fetching fraud warnings:", err);
        setError("Failed to load fraud warnings");
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [riskLevel, warningType, notificationsEnabled]
  );

  // Helper functions
  const convertToCSV = (data) => {
    if (data.length === 0) return "";

    const headers = Object.keys(data[0]);
    const csvRows = [
      headers.join(","),
      ...data.map((row) =>
        headers
          .map((header) => {
            const value = row[header] || "";
            if (
              typeof value === "string" &&
              (value.includes(",") ||
                value.includes('"') ||
                value.includes("\n"))
            ) {
              return `"${value.replace(/"/g, '""')}"`;
            }
            return value;
          })
          .join(",")
      ),
    ];

    return csvRows.join("\n");
  };

  const downloadCSV = (content, filename) => {
    const blob = new Blob([content], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    link.click();
    URL.revokeObjectURL(link.href);
  };

  const downloadJSON = (content, filename) => {
    const blob = new Blob([content], {
      type: "application/json;charset=utf-8;",
    });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    link.click();
    URL.revokeObjectURL(link.href);
  };

  useEffect(() => {
    fetchWarnings(true);
  }, [environment, fetchWarnings]);

  useEffect(() => {
    if (notificationsEnabled) {
      const interval = setInterval(() => {
        fetchWarnings(true);
      }, 60000);

      return () => clearInterval(interval);
    }
  }, [notificationsEnabled, fetchWarnings]);

  useEffect(() => {
    setPage(0);
  }, [riskLevel, warningType, statusFilter]);

  // Listen for external refresh requests
  useEffect(() => {
    const handleExternalRefresh = () => {
      fetchWarnings(true);
    };
    window.addEventListener("refresh-fraud-warnings", handleExternalRefresh);
    return () => {
      window.removeEventListener("refresh-fraud-warnings", handleExternalRefresh);
    };
  }, [fetchWarnings]);

  // All the existing handler functions would stay the same...
  const handleRefresh = () => {
    fetchWarnings(true);
  };

  const toggleRowExpanded = (rowId) => {
    setExpandedRows((prev) => ({
      ...prev,
      [rowId]: !prev[rowId],
    }));
  };

  const handleReviewClick = (warning) => {
    setReviewDialog({
      open: true,
      warning,
      notes: "",
    });
  };

  const handleReviewSubmit = async (action) => {
    if (!reviewDialog.warning) return;

    const { warning } = reviewDialog;
    const notes = reviewDialog.notes || "";

    try {
      setError(null);

      if (action === "review") {
        // This is "Add Notes" - changes status to "under-review"
        if (!notes.trim()) {
          setError("Please enter notes before adding them.");
          return;
        }

        // ✅ CRITICAL: Use the orderNumber (which is the KV key) not the id
        const warningKey = warning.orderNumber || warning.id;
        console.log("🔍 Using warning key for add notes:", warningKey);

        const response = await apiClient.addNotesToFraudWarning(
          warningKey,
          notes.trim()
        );

        if (response && response.success) {
          setError({
            severity: "success",
            message:
              "Notes added successfully - status changed to Under Review",
          });
        } else {
          setError(
            "Failed to add notes: " + (response?.message || "Unknown error")
          );
        }
      } else {
        // This is a status-changing action (ok, block, block-plus, ban)
        if ((action === "block" || action === "block-plus" || action === "ban") && !notes.trim()) {
          setError("Please provide notes before blocking");
          return;
        }

        // Ban action: call dedicated ban endpoint
        if (action === "ban") {
          try {
            const banResponse = await apiClient.banFraudWarning(
              warning.orderNumber || warning.id,
              {
                reason: notes.trim(),
              }
            );

            if (banResponse && banResponse.success) {
              setError({
                severity: "success",
                message: `Warning banned and added to chargebacks successfully`,
              });
            } else {
              setError(
                "Failed to ban: " + (banResponse?.message || "Unknown error")
              );
            }
          } catch (banError) {
            console.error("Error banning order:", banError);
            setError({
              severity: "error",
              message: `Failed to ban order: ${banError.message}`,
            });
          }

          // Clear the dialog and refresh warnings
          setReviewDialog({
            open: false,
            warning: null,
            notes: "",
          });

          apiClient.clearCacheForEndpoint("/api/admin/fraud-warnings");
          await fetchWarnings(true, {
            force: true,
            timestamp: Date.now(),
            skipCache: true,
            forceReload: true,
          });
          return;
        }

        let status = "under-review";
        switch (action) {
          case "ok":
            status = "reviewed-ok";
            break;
          case "block":
            status = "blocked";
            break;
          case "block-plus":
            status = "blocked-plus";
            break;
          default:
            status = "under-review";
        }

        const reviewData = {
          warningId: warning.orderNumber || warning.id, // Use orderNumber as KV key
          notes: notes.trim(),
          warningStatus: status,
          action: action,
          timestamp: new Date().toISOString(),
          reviewedBy: "admin",
        };

        console.log("Submitting review with data:", reviewData);

        const response = await apiClient.reviewFraudWarning(
          warning.orderNumber || warning.id,
          reviewData
        );

        if (response && response.success) {
          setError({
            severity: "success",
            message: `Warning ${getActionText(action)} successfully`,
          });
        } else {
          setError(
            "Failed to submit review: " + (response?.message || "Unknown error")
          );
        }

        // Add to fraud database when blocking
        if (action === "block" || action === "block-plus") {
          try {
            // Also add to fraud database when blocking
            const addToDatabaseResponse = await apiClient.addFraudWarningToDatabase(
              warning.orderNumber || warning.id,
              {
                reviewNotes: notes.trim(),
                blockReason: action === "block-plus" ? "High-risk fraud - added to database" : "Manual review block",
                email: warning.email,
                riskLevel: warning.riskLevel
              }
            );

            if (addToDatabaseResponse && addToDatabaseResponse.success) {
              setError({
                severity: "success",
                message: `Warning ${getActionText(action)} and added to fraud database successfully`,
              });
            } else {
              // Still show success for the block, but note database issue
              setError({
                severity: "warning",
                message: `Warning ${getActionText(action)} successfully, but there was an issue adding to the fraud database`,
              });
            }
          } catch (dbError) {
            console.error("Error adding to fraud database:", dbError);
            // Still show success for the block action
            setError({
              severity: "warning",
              message: `Warning ${getActionText(action)} successfully, but failed to add to fraud database`,
            });
          }
        }
      }

      // Clear the dialog and refresh warnings
      setReviewDialog({
        open: false,
        warning: null,
        notes: "",
      });

      // Clear cache and refresh
      apiClient.clearCacheForEndpoint("/api/admin/fraud-warnings");
      await fetchWarnings(true, {
        force: true,
        timestamp: Date.now(),
        skipCache: true,
        forceReload: true,
      });
    } catch (err) {
      console.error("Error submitting review:", err);
      setError("Failed to submit review: " + err.message);
    }
  };

  const handleClearAllWarnings = async () => {
    if (environment !== "staging") {
      alert("Warning clearing is only available in staging environment!");
      return;
    }

    setClearWarningsDialog(true);
  };

  const confirmClearAllWarnings = async () => {
    setClearingWarnings(true);
    setError(null);

    try {
      if (typeof apiClient.clearAllFraudWarnings === "function") {
        await apiClient.clearAllFraudWarnings();
      }

      setWarnings([]);
      await fetchWarnings(true, { forceReload: true });

      setClearWarningsDialog(false);
      alert("Fraud warnings cleared successfully");
    } catch (err) {
      console.error("Error clearing warnings:", err);
      setError({
        severity: "error",
        message: "Error clearing warnings: " + err.message,
      });
    } finally {
      setClearingWarnings(false);
      setClearWarningsDialog(false);
    }
  };

  const toggleNotifications = () => {
    setNotificationsEnabled(!notificationsEnabled);
    if (!notificationsEnabled) {
      if ("Notification" in window && Notification.permission === "default") {
        Notification.requestPermission();
      }
    }
  };

  const showNotification = (message) => {
    if ("Notification" in window && Notification.permission === "granted") {
      new Notification("Fraud Detection Dashboard", {
        body: message,
        icon: "/favicon.ico",
      });
    }
  };

  const getActionText = (action) => {
    switch (action) {
      case "ok":
        return "marked as OK";
      case "block":
        return "blocked";
      case "block-plus":
        return "blocked and added to fraud database";
      case "ban":
        return "banned and added to chargebacks";
      default:
        return "reviewed";
    }
  };

  const renderExportButton = () => (
    <>
      <Button
        variant="outlined"
        size="small"
        startIcon={<DownloadIcon />}
        onClick={handleExportClick}
        disabled={loading || exporting}
        sx={{ ml: 1 }}
      >
        {exporting ? "Exporting..." : "Export"}
      </Button>

      <Menu
        anchorEl={exportMenuAnchor}
        open={Boolean(exportMenuAnchor)}
        onClose={handleExportClose}
        PaperProps={{
          sx: { minWidth: 250 },
        }}
      >
        <MenuItem onClick={() => exportToCSV(false)}>
          <ListItemIcon>
            <TableChartIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary="Export Displayed as CSV"
            secondary={`${filteredWarnings.length} warnings`}
          />
        </MenuItem>

        <MenuItem onClick={() => exportToJSON(false)}>
          <ListItemIcon>
            <FileDownloadIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary="Export Displayed as JSON"
            secondary={`${filteredWarnings.length} warnings`}
          />
        </MenuItem>

        <Divider />

        <MenuItem onClick={() => exportToCSV(true)}>
          <ListItemIcon>
            <TableChartIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary="Export ALL Data as CSV"
            secondary={`${warnings.length} warnings (includes legacy)`}
          />
        </MenuItem>

        <MenuItem onClick={() => exportToJSON(true)}>
          <ListItemIcon>
            <FileDownloadIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary="Export ALL Data as JSON"
            secondary={`${warnings.length} warnings (includes legacy)`}
          />
        </MenuItem>
      </Menu>
    </>
  );

  const getRiskLevelChip = (level) => {
    switch (safeToLowerCase(level)) {
      case "critical":  // ✅ ADD THIS CASE FIRST
        return (
          <Chip
            icon={
              <Box component="span" sx={{ fontSize: "0.8em" }}>
                🚨
              </Box>
            }
            label="CRITICAL"
            size="small"
            color="error"
            sx={{ fontWeight: "bold" }}
          />
        );
      case "high":
        return (
          <Chip
            icon={
              <Box component="span" sx={{ fontSize: "0.8em" }}>
                ⚠️
              </Box>
            }
            label="High Risk"
            size="small"
            color="error"
          />
        );
      case "medium":
        return (
          <Chip
            icon={
              <Box component="span" sx={{ fontSize: "0.8em" }}>
                ⚠️
              </Box>
            }
            label="Medium Risk"
            size="small"
            color="warning"
          />
        );
      case "low":
        return (
          <Chip
            icon={
              <Box component="span" sx={{ fontSize: "0.8em" }}>
                ⚠️
              </Box>
            }
            label="Low Risk"
            size="small"
            color="info"
          />
        );
      default:
        return <Chip icon={<InfoIcon />} label="Unknown" size="small" />;
    }
  };

  const getStatusChip = (status) => {
    const statusKey = safeToLowerCase(status) || "new";

    switch (statusKey) {
      case "under-review":
      case "review":
      case "reviewed":
        return (
          <Chip
            icon={<CommentIcon />}
            label="Under Review"
            size="small"
            color="warning"
          />
        );
      case "reviewed-ok":
        return (
          <Chip
            icon={<CheckCircleIcon />}
            label="Reviewed - OK"
            size="small"
            color="success"
          />
        );
      case "blocked":
        return (
          <Chip
            icon={<BlockIcon />}
            label="BLOCKED"
            size="small"
            color="error"
          />
        );
      case "blocked-plus":
        return (
          <Chip
            icon={<LockIcon />}
            label="BLOCKED+"
            size="small"
            color="error"
            sx={{
              fontWeight: "bold",
              backgroundColor: "#b71c1c",
            }}
          />
        );
      case "new":
      default:
        return (
          <Chip icon={<InfoIcon />} label="New" size="small" color="info" />
        );
    }
  };

  const getRowBackgroundColor = (warning) => {
    const status = safeToLowerCase(warning?.status) || "new";

    switch (status) {
      case "blocked":
      case "blocked-plus":
        return "rgba(211, 47, 47, 0.08)";
      case "reviewed-ok":
        return "rgba(76, 175, 80, 0.08)";
      case "under-review":
      case "review":
        return "rgba(255, 152, 0, 0.08)";
      case "new":
        return "rgba(33, 150, 243, 0.05)";
      default:
        return "inherit";
    }
  };

  const getFilteredWarnings = () => {
    let filtered = warnings;

    // ✅ NEW: Filter at the "door" - only show COMPLETED sessions
    filtered = filtered.filter((warning) => {
      const sessionState = warning.sessionInfo?.sessionState || warning.sessionState;
      const isCompleted = sessionState === "COMPLETED";

      // Alternative check: if sessionState is not available, check storage key
      const hasSONW =
        warning.storageKey?.includes("SONW") ||
        warning.sessionId?.includes("SONW") ||
        warning.id?.includes("SONW");

      const isDatabaseMatch = warning.type === 'database_match';


  const shouldDisplay =
    isCompleted || 
    (sessionState === undefined && hasSONW) ||
    isDatabaseMatch;  // ✅ ADD THIS LINE


      // Debug logging for transparency
      if (!shouldDisplay && process.env.NODE_ENV === "development") {
        console.debug("Panel filtering out non-COMPLETED session:", {
          warningId: warning.id,
          orderNumber: warning.orderNumber,
          sessionState: sessionState,
          hasSONW: hasSONW,
          isDatabaseMatch: isDatabaseMatch,  // ✅ ADD THIS
          reason: "Not a COMPLETED session",
        });
      }

      return shouldDisplay;
    });

    if (statusFilter !== "all") {
      filtered = filtered.filter((warning) => {
        const warningStatus = safeToLowerCase(warning?.status) || "new";

        switch (statusFilter) {
          case "all-except-reviewed":
            return warningStatus !== "reviewed-ok";
          case "new":
            return warningStatus === "new";
          case "under-review":
            return (
              warningStatus === "under-review" ||
              warningStatus === "review" ||
              warningStatus === "reviewed"
            );
          case "reviewed-ok":
            return warningStatus === "reviewed-ok";
          case "blocked":
            return warningStatus === "blocked";
          case "blocked-plus":
            return warningStatus === "blocked-plus";
          default:
            return true;
        }
      });
    }

    if (riskLevel !== "all") {
      filtered = filtered.filter((w) => w.riskLevel === riskLevel);
    }
    if (warningType !== "all") {
      filtered = filtered.filter((w) => w.type === warningType);
    }

    filtered.sort((a, b) => {
      const dateA = new Date(a.timestamp);
      const dateB = new Date(b.timestamp);
      return dateB - dateA;
    });

    return filtered;
  };

  const getAllFilteredWarnings = getFilteredWarnings();
  const paginatedWarnings = getAllFilteredWarnings.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const filteredWarnings = getFilteredWarnings();

  // ✅ ADD ALL THESE EXPORT FUNCTIONS:
  const handleExportClick = (event) => {
    setExportMenuAnchor(event.currentTarget);
  };

  const handleExportClose = () => {
    setExportMenuAnchor(null);
  };

  const exportToCSV = useCallback(
    async (includeAllData = false) => {
      try {
        setExporting(true);
        setExportMenuAnchor(null);

        let dataToExport;
        if (includeAllData) {
          console.log(
            "Exporting ALL fraud warnings (including legacy data)..."
          );
          dataToExport = warnings;
        } else {
          console.log("Exporting currently displayed fraud warnings...");
          dataToExport = filteredWarnings;
        }

        if (dataToExport.length === 0) {
          alert("No data to export");
          return;
        }

        const csvData = dataToExport.map((warning) => ({
          Timestamp: formatTimestamp(warning.timestamp),
          "Order Number": warning.orderNumber || "",
          "Customer Name": warning.customerName || "",
          "Customer Email": warning.email || warning.customerEmail || "",
          "Warning Type": formatWarningType(warning.type, warning.warningType),
          "Risk Level": warning.riskLevel || "",
          Amount: warning.amount || warning.billingInfo?.amount || "",
          Status: warning.status || "new",
          Reason: warning.reason || "",
          "IP Address": warning.ipAddress || "",

          // Billing Information
          "Billing Name": warning.billingInfo
            ? `${warning.billingInfo.first_name || ""} ${warning.billingInfo.last_name || ""
              }`.trim()
            : "",
          "Billing Email": warning.billingInfo?.email || "",
          "Billing Phone": warning.billingInfo?.phone || "",
          "Billing Address": warning.billingInfo?.address1 || "",
          "Billing City": warning.billingInfo?.city || "",
          "Billing State": warning.billingInfo?.state || "",
          "Billing ZIP": warning.billingInfo?.zip || "",
          "Billing Country": warning.billingInfo?.country || "",

          // Shipping Information
          "Shipping Name": warning.shippingInfo
            ? `${warning.shippingInfo.first_name || ""} ${warning.shippingInfo.last_name || ""
              }`.trim()
            : "",
          "Shipping Email": warning.shippingInfo?.email || "",
          "Shipping Phone": warning.shippingInfo?.phone || "",
          "Shipping Address": warning.shippingInfo?.address1 || "",
          "Shipping City": warning.shippingInfo?.city || "",
          "Shipping State": warning.shippingInfo?.state || "",
          "Shipping ZIP": warning.shippingInfo?.zip || "",
          "Shipping Country": warning.shippingInfo?.country || "",

          // Differences
          "Names Differ": warning.differences?.namesDiffer ? "Yes" : "No",
          "Emails Differ": warning.differences?.emailsDiffer ? "Yes" : "No",
          "Phones Differ": warning.differences?.phonesDiffer ? "Yes" : "No",
          "Addresses Differ": warning.differences?.addressesDiffer
            ? "Yes"
            : "No",
          "Countries Differ": warning.differences?.differentCountries
            ? "Yes"
            : "No",

          // Review Information
          "Review Notes": warning.reviewNotes || "",
          "Reviewed At": warning.reviewedAt
            ? formatTimestamp(warning.reviewedAt)
            : "",
          "Reviewed By": warning.reviewedBy || "",

          // Technical Information
          "Warning ID": warning.id || warning.warningId || "",
          "Session ID": warning.sessionId || "",
          "Storage Key": warning.storageKey || "",
          "Session State":
            warning.sessionInfo?.sessionState || warning.sessionState || "",
          "Data Format": warning.warningId?.startsWith("fraud_warning_")
            ? "Legacy"
            : "New",
          Environment: warning.environment || "",
        }));

        const csvContent = convertToCSV(csvData);

        const timestamp = new Date()
          .toISOString()
          .slice(0, 19)
          .replace(/:/g, "-");
        const filename = includeAllData
          ? `fraud-warnings-ALL-${timestamp}.csv`
          : `fraud-warnings-filtered-${timestamp}.csv`;

        downloadCSV(csvContent, filename);

        console.log(
          `✅ Exported ${dataToExport.length} fraud warnings to ${filename}`
        );
      } catch (error) {
        console.error("Error exporting fraud warnings:", error);
        alert("Error exporting data: " + error.message);
      } finally {
        setExporting(false);
      }
    },
    [warnings, filteredWarnings]
  );

  const exportToJSON = useCallback(
    async (includeAllData = false) => {
      try {
        setExporting(true);
        setExportMenuAnchor(null);

        const dataToExport = includeAllData ? warnings : filteredWarnings;

        if (dataToExport.length === 0) {
          alert("No data to export");
          return;
        }

        const exportData = {
          exportedAt: new Date().toISOString(),
          exportType: includeAllData ? "all_data" : "filtered_display",
          totalRecords: dataToExport.length,
          environment: environment,
          filters: includeAllData
            ? null
            : {
              statusFilter,
              riskLevel: riskLevel !== "all" ? riskLevel : null,
              warningType: warningType !== "all" ? warningType : null,
            },
          warnings: dataToExport,
        };

        const jsonContent = JSON.stringify(exportData, null, 2);

        const timestamp = new Date()
          .toISOString()
          .slice(0, 19)
          .replace(/:/g, "-");
        const filename = includeAllData
          ? `fraud-warnings-ALL-${timestamp}.json`
          : `fraud-warnings-filtered-${timestamp}.json`;

        downloadJSON(jsonContent, filename);

        console.log(
          `✅ Exported ${dataToExport.length} fraud warnings to ${filename}`
        );
      } catch (error) {
        console.error("Error exporting fraud warnings:", error);
        alert("Error exporting data: " + error.message);
      } finally {
        setExporting(false);
      }
    },
    [
      warnings,
      filteredWarnings,
      environment,
      statusFilter,
      riskLevel,
      warningType,
    ]
  );

  // Add this after the filtering to show the effect
  React.useEffect(() => {
    if (warnings.length > 0) {
      const totalWarnings = warnings.length;
      const displayedWarnings = filteredWarnings.length;
      const hiddenWarnings = totalWarnings - displayedWarnings;

      console.log("Fraud Warnings Panel Display Filter:", {
        totalReceived: totalWarnings,
        displayed: displayedWarnings,
        hiddenByCompletedFilter: hiddenWarnings,
        filterLocation: "Panel Level (Door)",
        preservesDataPipeline: true,
      });
    }
  }, [warnings, filteredWarnings]);

  return (
    <Paper
      id="panel-fraud-warnings"
      sx={{
        p: 0, // Remove default padding
        mb: 3,
        // Keep the existing yellow border
        borderTop: "5px solid #eab308",
        boxShadow:
          "0 -2px 4px rgba(234, 179, 8, 0.1), 0 2px 8px rgba(0,0,0,0.1)",
        // ADD: Full bleed pale yellow background
        overflow: "hidden",
      }}
    >
      {/* Header Section - Full bleed */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        sx={{
          // ADD: Pale yellow background only for header
          backgroundColor: "#f9e696",
          padding: "20px 24px 16px 24px",
          mb: 0, // Remove margin bottom since we want seamless transition
        }}
      >
        <Typography variant="h5" component="h2">
          <Box display="flex" alignItems="center" gap={1}>
            {/* Shield icon from email header */}
            <Box
              component="span"
              sx={{
                fontSize: "1.5em",
                filter: "drop-shadow(1px 1px 2px rgba(0,0,0,0.2))",
              }}
            >
              🛡️
            </Box>
            <Box>
              <Box component="span" fontWeight="bold">
                Fraud Warnings
                {environment === "staging" && (
                  <Chip
                    label="STAGING"
                    size="small"
                    color="warning"
                    sx={{ ml: 1 }}
                  />
                )}
              </Box>
            </Box>
          </Box>
        </Typography>
        <Box display="flex" alignItems="center" gap={1}>
          {lastUpdated && (
            <Typography variant="caption" color="text.secondary">
              Updated: {formatTimestamp(lastUpdated)}
            </Typography>
          )}

          {environment === "staging" && (
            <Button
              variant="contained"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={handleClearAllWarnings}
              disabled={clearingWarnings || refreshing}
              size="small"
            >
              {clearingWarnings ? "Clearing..." : "Clear All"}
            </Button>
          )}

          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={handleRefresh}
            disabled={refreshing}
            size="small"
          >
            {refreshing ? "Refreshing..." : "Refresh"}
          </Button>
          {/* Export button moved to bottom of panel */}
        </Box>
      </Box>
      <Typography sx={{ fontSize: '1rem', fontWeight: 'normal', px: 3, pb: 1, backgroundColor: '#f9e696' }} color="text.secondary">
        Logged Warnings for Address Mismatch, Identity Plausibility and Database Match. Does NOT block order.
      </Typography>

      {/* Main Content Section - With padding */}
      <Box sx={{ px: 3, pb: 3, pt: 2, backgroundColor: "white" }}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          sx={{ width: "100%", mb: 2 }}
        >
          {/* Left side: Filter controls grouped together */}
          <Box display="flex" alignItems="center" gap={2}>
            <FormControl size="small" sx={{ width: 140 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="all">All Statuses</MenuItem>
                <MenuItem value="all-except-reviewed">
                  All (except Reviewed)
                </MenuItem>
                <MenuItem value="new">New</MenuItem>
                <MenuItem value="under-review">Under Review</MenuItem>
                <MenuItem value="reviewed-ok">Reviewed - OK</MenuItem>
                <MenuItem value="blocked">BLOCKED</MenuItem>
                <MenuItem value="blocked-plus">BLOCKED+</MenuItem>
              </Select>
            </FormControl>

            <FormControl sx={{ minWidth: 120 }}>
              <InputLabel>Risk Level</InputLabel>
              <Select
                value={riskLevel}
                label="Risk Level"
                onChange={(e) => setRiskLevel(e.target.value)}
                size="small"
              >
                <MenuItem value="all">All Levels</MenuItem>
                <MenuItem value="critical">Critical</MenuItem>
                <MenuItem value="high">High</MenuItem>
                <MenuItem value="medium">Medium</MenuItem>
                <MenuItem value="low">Low</MenuItem>
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ width: 120 }}>
              <InputLabel>Warning Type</InputLabel>
              <Select
                value={warningType}
                label="Warning Type"
                onChange={(e) => setWarningType(e.target.value)}
              >
                <MenuItem value="all">All Types</MenuItem>
                <MenuItem value="address">Address Mismatch</MenuItem>
                <MenuItem value="database_match">Database Match</MenuItem>
                <MenuItem value="identity_plausibility">Identity Plausibility</MenuItem>
              </Select>
            </FormControl>
          </Box>

          {/* Right side: Pagination */}
          <Box sx={{ flexShrink: 0 }}>
            <TablePagination
              rowsPerPageOptions={[25, 50, 100, 150]}
              component="div"
              count={getAllFilteredWarnings.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              labelDisplayedRows={({ from, to, count }) =>
                `${from}-${to} of ${count} fraud warnings`
              }
              sx={{
                "& .MuiTablePagination-toolbar": {
                  minHeight: "40px",
                  paddingLeft: 0,
                  paddingRight: 0,
                },
                "& .MuiTablePagination-spacer": {
                  display: "none",
                },
                flexShrink: 0,
              }}
            />
          </Box>
        </Box>
        {/* Content */}
        {loading && !refreshing ? (
          <Box display="flex" justifyContent="center" my={4}>
            <CircularProgress />
          </Box>
        ) : error && typeof error === "object" ? (
          <Alert severity={error.severity || "error"} sx={{ my: 2 }}>
            {error.message}
          </Alert>
        ) : error ? (
          <Alert severity="error" sx={{ my: 2 }}>
            {error}
          </Alert>
        ) : (
          <>
            <TableContainer
              sx={{
                border: "1px solid #e5e7eb",
                borderRadius: "6px",
                overflow: "hidden",
              }}
            >
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: "#f9fafb" }}>
                    <TableCell
                      padding="checkbox"
                      sx={{ backgroundColor: "#f9fafb" }}
                    ></TableCell>
                    <TableCell
                      sx={{ backgroundColor: "#f9fafb", fontWeight: 600 }}
                    >
                      Timestamp
                    </TableCell>
                    <TableCell
                      sx={{ backgroundColor: "#f9fafb", fontWeight: 600 }}
                    >
                      Warning Type
                    </TableCell>
                    <TableCell
                      sx={{ backgroundColor: "#f9fafb", fontWeight: 600 }}
                    >
                      Order #
                    </TableCell>
                    <TableCell
                      sx={{ backgroundColor: "#f9fafb", fontWeight: 600 }}
                    >
                      Name
                    </TableCell>
                    <TableCell
                      sx={{ backgroundColor: "#f9fafb", fontWeight: 600 }}
                    >
                      Amount
                    </TableCell>
                    <TableCell
                      sx={{ backgroundColor: "#f9fafb", fontWeight: 600 }}
                    >
                      Risk Level
                    </TableCell>
                    <TableCell
                      sx={{ backgroundColor: "#f9fafb", fontWeight: 600 }}
                    >
                      Reason
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{ backgroundColor: "#f9fafb", fontWeight: 600 }}
                    >
                      Status
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{ backgroundColor: "#f9fafb", fontWeight: 600 }}
                    >
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedWarnings.length > 0 ? (
                    paginatedWarnings.map((warning, index) => (
                      <React.Fragment key={warning.id}>
                        <TableRow
                          hover
                          sx={{
                            bgcolor: getRowBackgroundColor(warning),
                            "&:hover": {
                              backgroundColor: "#f9fafb",
                            },
                            borderBottom: "1px solid #e5e7eb",
                          }}
                        >
                          <TableCell
                            padding="checkbox"
                            sx={{ borderBottom: "1px solid #e5e7eb" }}
                          >
                            <IconButton
                              size="small"
                              onClick={() => toggleRowExpanded(warning.id)}
                            >
                              {expandedRows[warning.id] ? (
                                <Box
                                  component="span"
                                  sx={{ fontSize: "1.2em" }}
                                >
                                  ▲
                                </Box>
                              ) : (
                                <Box
                                  component="span"
                                  sx={{ fontSize: "1.2em" }}
                                >
                                  ▼
                                </Box>
                              )}
                            </IconButton>
                          </TableCell>
                          <TableCell sx={{ borderBottom: "1px solid #e5e7eb" }}>
                            {formatTimestamp(warning.timestamp)}
                          </TableCell>
                          <TableCell sx={{ borderBottom: "1px solid #e5e7eb" }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              {warning.type === 'database_match' ? (
                                <PersonSearchIcon fontSize="small" color="primary" />
                              ) : warning.type === 'identity_plausibility' ? (
                                <GroupSharp fontSize="small" color="info" />
                              ) : (
                                <WarningIcon fontSize="small" color="warning" />
                              )}
                              {formatWarningType(warning.type, warning.warningType)}
                            </Box>
                          </TableCell>
                          <TableCell sx={{ borderBottom: "1px solid #e5e7eb" }}>
                            {warning.orderNumber}
                          </TableCell>
                          <TableCell sx={{ borderBottom: "1px solid #e5e7eb" }}>
                            {warning.customerName}
                          </TableCell>
                          <TableCell sx={{ borderBottom: "1px solid #e5e7eb" }}>
                            {warning.amount ||
                              warning.orderAmount ||
                              (warning.billingInfo &&
                                warning.billingInfo.amount) ? (
                              <Box display="flex" alignItems="center">
                                <AttachMoneyIcon
                                  fontSize="x-small"
                                  sx={{ mr: 0.5 }}
                                />
                                {new Intl.NumberFormat("en-US", {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2,
                                }).format(
                                  warning.amount || warning.orderAmount || warning.billingInfo.amount
                                )}
                              </Box>
                            ) : (
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                -
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell sx={{ borderBottom: "1px solid #e5e7eb" }}>
                            {getRiskLevelChip(warning.riskLevel)}
                          </TableCell>
                          <TableCell sx={{ borderBottom: "1px solid #e5e7eb" }}>
                            {warning.type === 'database_match' ? (
                              <Box>
                                <strong>Match({warning.matchCount || 0})</strong>
                                {' - '}
                                Matched {warning.matchedRecordCount || 0} fraud record
                                {warning.matchedRecordCount !== 1 ? 's' : ''}
                              </Box>
                            ) : (
                              warning.reason
                            )}
                          </TableCell>
                          <TableCell
                            align="center"
                            sx={{ borderBottom: "1px solid #e5e7eb" }}
                          >
                            {getStatusChip(warning.status)}
                          </TableCell>
                          <TableCell
                            align="center"
                            sx={{ borderBottom: "1px solid #e5e7eb" }}
                          >
                            <Button
                              size="small"
                              variant="outlined"
                              color="primary"
                              onClick={() => handleReviewClick(warning)}
                              disabled={
                                safeToLowerCase(warning?.status) ===
                                "blocked" ||
                                safeToLowerCase(warning?.status) ===
                                "blocked-plus"
                              }
                            >
                              {safeToLowerCase(warning?.status) === "blocked" ||
                                safeToLowerCase(warning?.status) ===
                                "blocked-plus"
                                ? "Blocked"
                                : "Review"}
                            </Button>
                          </TableCell>
                        </TableRow>

                        <TableRow>
                          <TableCell
                            colSpan={10}
                            style={{ paddingBottom: 0, paddingTop: 0 }}
                          >
                            <Collapse
                              in={expandedRows[warning.id]}
                              timeout="auto"
                              unmountOnExit
                            >
                              <Box>
                                {/* ✅ ADD CONDITIONAL RENDERING */}
                                {warning.type === 'database_match' ? (
                                  <DatabaseMatchDetails warning={warning} />
                                ) : warning.type === 'identity_plausibility' ? (
                                  <IdentityPlausibilityDetails warning={warning} />
                                ) : (
                                  <ComparisonDetails
                                    billingInfo={warning.billingInfo}
                                    shippingInfo={warning.shippingInfo}
                                  />
                                )}
                                <Divider sx={{ my: 2 }} />
                                {/* Add debugging info in development */}
                                {process.env.NODE_ENV === "development" && (
                                  <Box
                                    mb={2}
                                    p={1}
                                    bgcolor="#f0f0f0"
                                    fontSize="0.8rem"
                                  >
                                    <strong>DEBUG Notes Data:</strong>
                                    <pre>
                                      {JSON.stringify(
                                        {
                                          warningId: warning.id,
                                          hasStatusHistory: !!(
                                            warning.statusHistory &&
                                            warning.statusHistory.length > 0
                                          ),
                                          statusHistoryLength:
                                            warning.statusHistory?.length || 0,
                                          hasReviewNotes: !!warning.reviewNotes,
                                          reviewNotesLength:
                                            warning.reviewNotes?.length || 0,
                                        },
                                        null,
                                        2
                                      )}
                                    </pre>
                                  </Box>
                                )}
                                <NotesHistory warning={warning} />
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      </React.Fragment>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell
                        colSpan={10}
                        align="center"
                        sx={{ borderBottom: "1px solid #e5e7eb" }}
                      >
                        No fraud warnings found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[25, 50, 100, 150]}
              component="div"
              count={getAllFilteredWarnings.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              labelDisplayedRows={({ from, to, count }) =>
                `${from}-${to} of ${count} fraud warnings`
              }
            />

            {/* All dialog components would stay the same... */}
            <Dialog
              open={reviewDialog.open}
              onClose={() =>
                setReviewDialog({ open: false, warning: null, notes: "" })
              }
              maxWidth="md"
              fullWidth
            >
              <DialogTitle>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography variant="h6">Review Warning</Typography>
                  {reviewDialog.warning && (
                    <Chip
                      label={reviewDialog.warning.orderNumber}
                      size="small"
                      color="primary"
                    />
                  )}
                </Box>
              </DialogTitle>
              <DialogContent>
                {reviewDialog.warning && (
                  <Box mb={2}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="subtitle2" color="text.secondary">
                          Customer
                        </Typography>
                        <Typography variant="body1">
                          {reviewDialog.warning.customerName}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="subtitle2" color="text.secondary">
                          Order Amount
                        </Typography>
                        <Typography variant="body1">
                          {(() => {
                            const amount =
                              reviewDialog.warning?.amount ||
                              reviewDialog.warning?.billingInfo?.amount ||
                              reviewDialog.warning?.billingInfo?.orderTotal ||
                              0;

                            return amount > 0
                              ? `$${amount.toFixed(2)}`
                              : "Not available";
                          })()}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="subtitle2" color="text.secondary">
                          Risk Level
                        </Typography>
                        {getRiskLevelChip(reviewDialog.warning.riskLevel)}
                      </Grid>
                    </Grid>
                  </Box>
                )}

                {reviewDialog.warning?.statusHistory?.length > 0 && (
                  <Box mb={2}>
                    <NotesHistory warning={reviewDialog.warning} />
                    <Divider sx={{ my: 2 }} />
                  </Box>
                )}

                <TextField
                  autoFocus
                  margin="dense"
                  label="Add Review Notes"
                  type="text"
                  fullWidth
                  multiline
                  rows={4}
                  value={reviewDialog.notes}
                  onChange={(e) => {
                    setReviewDialog((prev) => ({
                      ...prev,
                      notes: e.target.value,
                    }));
                  }}
                  placeholder="Enter your review notes here..."
                  error={
                    reviewDialog.action &&
                    ["block", "block-plus", "ban"].includes(reviewDialog.action) &&
                    !reviewDialog.notes.trim()
                  }
                  helperText={
                    reviewDialog.action &&
                      ["block", "block-plus", "ban"].includes(reviewDialog.action) &&
                      !reviewDialog.notes.trim()
                      ? "Notes are required when blocking"
                      : "Notes will be added to the cumulative history"
                  }
                />
              </DialogContent>
              <DialogActions>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  width="100%"
                  px={2}
                >
                  <Box>
                    <Button
                      onClick={() =>
                        setReviewDialog({
                          open: false,
                          warning: null,
                          notes: "",
                        })
                      }
                    >
                      Cancel
                    </Button>
                  </Box>
                  <Box display="flex" gap={1}>
                    <Button
                      onClick={() => handleReviewSubmit("review")}
                      variant="outlined"
                      color="primary"
                      disabled={!reviewDialog.notes?.trim()}
                    >
                      Add Notes
                    </Button>
                    <Button
                      onClick={() => handleReviewSubmit("ok")}
                      variant="contained"
                      color="success"
                    >
                      Review - OK
                    </Button>
                    <Button
                      onClick={() => handleReviewSubmit("block")}
                      variant="contained"
                      color="error"
                      disabled={!reviewDialog.notes?.trim()}
                    >
                      Add to Fraud Warnings
                    </Button>
                    <Button
                      onClick={() => handleReviewSubmit("ban")}
                      variant="contained"
                      sx={{
                        backgroundColor: "#b71c1c",
                        "&:hover": {
                          backgroundColor: "#8e0000",
                        },
                      }}
                      disabled={!reviewDialog.notes?.trim()}
                    >
                      Ban
                    </Button>
                  </Box>
                </Box>
              </DialogActions>
            </Dialog>

            <Dialog
              open={clearWarningsDialog}
              onClose={() => !clearingWarnings && setClearWarningsDialog(false)}
            >
              <DialogTitle>
                <Box display="flex" alignItems="center" gap={1}>
                  <DeleteIcon color="error" />
                  Clear All Fraud Warnings
                </Box>
              </DialogTitle>
              <DialogContent>
                <Alert severity="warning" sx={{ mb: 2 }}>
                  <Typography variant="body1" fontWeight="bold">
                    You are about to delete ALL fraud warnings in the staging
                    environment.
                  </Typography>
                </Alert>
                <Typography variant="body2" paragraph>
                  This will clear all warnings data from the server and reset
                  the panel.
                </Typography>
              </DialogContent>
              <DialogActions>
                <Button
                  onClick={() => setClearWarningsDialog(false)}
                  disabled={clearingWarnings}
                >
                  Cancel
                </Button>
                <Button
                  onClick={confirmClearAllWarnings}
                  variant="contained"
                  color="error"
                  disabled={clearingWarnings}
                  startIcon={
                    clearingWarnings ? (
                      <CircularProgress size={20} />
                    ) : (
                      <DeleteIcon />
                    )
                  }
                >
                  {clearingWarnings ? "Clearing..." : "Yes, Clear All Warnings"}
                </Button>
              </DialogActions>
            </Dialog>
          </>
        )}
      </Box>
      <Box sx={{ px: 3, pb: 2, display: 'flex', justifyContent: 'flex-start' }}>
        {renderExportButton()}
      </Box>
    </Paper>
  );
};

export default FraudWarningsPanel;
